/**
 * Media helpers for gallery items.
 *
 * Video and YouTube are new item kinds, so this is where the rules about them
 * live: how a URL is recognised, what a privacy-preserving embed looks like, and
 * how a theme overlay cascades from site to page to item.
 */

import { ITEM_TYPES } from './schema.js';

/** Hosts we accept a video link from. */
const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'youtube-nocookie.com', 'www.youtube-nocookie.com'];
const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'];

/**
 * Pull the video id out of any of the URL shapes people paste.
 * @param {string} url
 * @returns {Object|null} { provider, id, start }
 */
export function parseEmbedUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return null;

    let parsed;
    try {
        parsed = new URL(url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`);
    } catch {
        return null;
    }

    const host = parsed.hostname.toLowerCase();
    const start = Number(parsed.searchParams.get('t') || parsed.searchParams.get('start') || 0) || 0;

    if (YOUTUBE_HOSTS.includes(host)) {
        // youtu.be/<id>
        if (host === 'youtu.be') {
            const id = parsed.pathname.slice(1).split('/')[0];
            return id ? { provider: 'youtube', id, start } : null;
        }
        // /watch?v=<id>
        const v = parsed.searchParams.get('v');
        if (v) return { provider: 'youtube', id: v, start };
        // /embed/<id>, /shorts/<id>, /live/<id>
        const match = parsed.pathname.match(/\/(embed|shorts|live|v)\/([^/?#]+)/);
        if (match) return { provider: 'youtube', id: match[2], start };
        return null;
    }

    if (VIMEO_HOSTS.includes(host)) {
        const match = parsed.pathname.match(/\/(?:video\/)?(\d+)/);
        return match ? { provider: 'vimeo', id: match[1], start } : null;
    }

    return null;
}

/**
 * @param {string} url
 * @returns {boolean} True when this looks like a video link we can embed
 */
export function isEmbeddableUrl(url) {
    return parseEmbedUrl(url) !== null;
}

/**
 * The iframe src for an embed.
 *
 * YouTube goes through youtube-nocookie.com, and the embed is only loaded after a
 * click (see the poster handling in the renderer), so a visitor who never plays a
 * video is not handed to Google.
 *
 * @param {Object} embed - { provider, id, start }
 * @param {Object} [options]
 * @returns {string|null}
 */
export function embedSrc(embed, { autoplay = false } = {}) {
    if (!embed?.id) return null;

    if (embed.provider === 'youtube') {
        const params = new URLSearchParams({
            rel: '0',
            modestbranding: '1',
            playsinline: '1',
            ...(autoplay ? { autoplay: '1' } : {}),
            ...(embed.start ? { start: String(embed.start) } : {}),
        });
        return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(embed.id)}?${params}`;
    }

    if (embed.provider === 'vimeo') {
        const params = new URLSearchParams({
            dnt: '1',
            ...(autoplay ? { autoplay: '1' } : {}),
            ...(embed.start ? { '#t': `${embed.start}s` } : {}),
        });
        return `https://player.vimeo.com/video/${encodeURIComponent(embed.id)}?${params}`;
    }

    return null;
}

/**
 * YouTube's own thumbnail, used when no poster has been chosen.
 * @param {Object} embed
 * @returns {string|null}
 */
export function embedThumbnail(embed) {
    if (embed?.provider === 'youtube' && embed.id) {
        return `https://i.ytimg.com/vi/${encodeURIComponent(embed.id)}/hqdefault.jpg`;
    }
    return null;
}

/**
 * @param {Object} file - A file record
 * @returns {boolean}
 */
export function isVideoFile(file) {
    return Boolean(file?.mimetype?.startsWith('video/'));
}

/**
 * Resolve the colours an item renders with: item overrides page, page overrides
 * site. Returns only what was actually set, so nothing is written unless asked.
 *
 * @param {Object} item - Gallery item
 * @param {Object} page - Page (or home)
 * @param {Object} theme - Compiled site theme
 * @returns {Object} { background, text, chrome }
 */
export function resolveItemTheme(item, page, theme) {
    const site = {
        background: theme?.colors?.background,
        text: theme?.colors?.text,
    };
    const pageTheme = {
        background: page?.theme?.background || page?.themeBackground || site.background,
        text: page?.theme?.text || page?.themeText || site.text,
    };

    return {
        background: item?.theme?.background || pageTheme.background,
        text: item?.theme?.text || pageTheme.text,
        // 'inherit' means "whatever the page decided", which is the default.
        chrome: item?.theme?.chrome || page?.theme?.chrome || 'inherit',
    };
}

/**
 * Build a gallery item from something dropped or pasted.
 *
 * Used by both the insert palette and the drop handler so a URL and a file end up
 * as the same shape either way.
 *
 * @param {Object} input - { url } or { file }
 * @returns {Object|null} A gallery item
 */
export function itemFromInput({ url, fileId, file }) {
    if (url) {
        const embed = parseEmbedUrl(url);
        if (!embed) return null;
        return {
            type: ITEM_TYPES.YOUTUBE,
            provider: embed.provider,
            videoId: embed.id,
            start: embed.start || undefined,
            url,
            posterFileId: null,
        };
    }

    if (fileId && file) {
        return isVideoFile(file)
            ? { type: ITEM_TYPES.VIDEO, fileId, posterFileId: null }
            : { type: ITEM_TYPES.IMAGE, fileId };
    }

    return null;
}

export default {
    parseEmbedUrl,
    isEmbeddableUrl,
    embedSrc,
    embedThumbnail,
    isVideoFile,
    resolveItemTheme,
    itemFromInput,
};
